import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { logJourney } from "@/lib/journey";
import "./AdvisorPremium.css";

const API = process.env.REACT_APP_BACKEND_URL;

const loadScript = (src) =>
  new Promise((res) => {
    if (document.querySelector(`script[src="${src}"]`)) return res(true);
    const s = document.createElement("script");
    s.src = src; s.onload = () => res(true); s.onerror = () => res(false);
    document.body.appendChild(s);
  });

/* Marketing / comparison flow that converts free advisors → the Premium listing.
 * The "Upgrade to Premium" CTA opens the mocked-Razorpay checkout wired to the
 * existing backend premium endpoints (pricing → initiate-order → verify-payment). */
export default function AdvisorPremium() {
  const navigate = useNavigate();
  const [checkout, setCheckout] = useState(false);
  useEffect(() => { logJourney("Advisor Marketplace", "Viewed", "Opened Premium upgrade flow"); }, []);
  const startUpgrade = () => { logJourney("Advisor Marketplace", "Upgrade started", "Clicked Upgrade to Premium"); setCheckout(true); };

  return (
    <div className="up" data-testid="advisor-premium-page">
      <div className="top">
        <div className="brand" onClick={() => navigate("/advisor-marketplace")}>IPO<span>Labs</span></div>
        <div className="back" onClick={() => navigate(-1)} data-testid="ap-premium-back"><i className="ti ti-arrow-left" /> Back</div>
      </div>

      <div className="hero">
        <div className="hero-k">Advisor listing · Premium</div>
        <div className="hero-h">Get seen first by the founders who need you.</div>
        <div className="hero-d">Premium advisors appear in the Featured band and rank above standard results — right where issuers start their search.</div>
        <div className="hero-proof">
          <div><div className="hp-v">5×</div><div className="hp-l">more profile views</div></div>
          <div><div className="hp-v">3×</div><div className="hp-l">more mandate requests</div></div>
          <div><div className="hp-v">~4 hrs</div><div className="hp-l">faster to first contact</div></div>
        </div>
      </div>

      <div className="demo">
        <div className="demo-t">Where your profile appears</div>
        <div className="demo-grid">
          <div className="dm-col">
            <div className="dm-lbl"><i className="ti ti-list" /> Free listing</div>
            {[["A1", "#1"], ["A2", "#2"], ["A3", "#3"]].map((m, i) => (
              <div className="mini-card" key={i}><div className="mc-av">{m[0]}</div><div className="mc-n">Advisor</div><div className="mc-pos">{m[1]}</div></div>
            ))}
            <div className="mini-card you"><div className="mc-av">YO</div><div className="mc-n">You</div><div className="mc-pos">#14</div></div>
            <div className="demo-note">Standard results · ranked by activity</div>
          </div>
          <div className="dm-col prem">
            <div className="dm-lbl g"><i className="ti ti-crown" /> Premium listing</div>
            <div className="mini-card you gold"><div className="mc-av" style={{ background: "#fbefd5", color: "#a86711" }}>YO</div><div className="mc-n">You <span className="mc-crown">★ Featured</span></div><div className="mc-pos">Top</div></div>
            {[["A1", "#1"], ["A2", "#2"], ["A3", "#3"]].map((m, i) => (
              <div className="mini-card" key={i}><div className="mc-av">{m[0]}</div><div className="mc-n">Advisor</div><div className="mc-pos">{m[1]}</div></div>
            ))}
            <div className="demo-note">Featured band · seen first by founders</div>
          </div>
        </div>
      </div>

      <div className="plans">
        <div className="plans-h">Choose your listing</div>
        <div className="plan-grid">
          <div className="plan" data-testid="plan-standard">
            <div className="plan-n">Standard</div>
            <div className="plan-price">Free</div>
            <div className="plan-sub">List and get discovered</div>
            <div className="feat yes"><i className="ti ti-check" /> Verified profile &amp; badges</div>
            <div className="feat yes"><i className="ti ti-check" /> Appear in search results</div>
            <div className="feat yes"><i className="ti ti-check" /> Receive mandate requests</div>
            <div className="feat yes"><i className="ti ti-check" /> Ratings &amp; reviews</div>
            <div className="feat no"><i className="ti ti-x" /> Featured-band placement</div>
            <div className="feat no"><i className="ti ti-x" /> Priority ranking</div>
            <div className="feat no"><i className="ti ti-x" /> Profile analytics</div>
            <button className="plan-btn pb-free" onClick={() => { toast.info("You're on the free Standard listing."); navigate("/advisor-marketplace"); }} data-testid="plan-standard-btn">Keep standard listing</button>
          </div>
          <div className="plan prem" data-testid="plan-premium">
            <div className="plan-tag">MOST CHOSEN</div>
            <div className="plan-n" style={{ color: "#185FA5" }}>Premium</div>
            <div className="plan-price">₹4,999<span>/yr</span></div>
            <div className="plan-sub">+ 18% GST · billed annually</div>
            <div className="feat yes"><i className="ti ti-check" /> Everything in Standard</div>
            <div className="feat star"><i className="ti ti-crown" /> Featured-band placement</div>
            <div className="feat star"><i className="ti ti-crown" /> Rank above standard results</div>
            <div className="feat star"><i className="ti ti-crown" /> "Premium" trust badge</div>
            <div className="feat yes"><i className="ti ti-check" /> Profile view &amp; contact analytics</div>
            <div className="feat yes"><i className="ti ti-check" /> Showcase deals &amp; case studies</div>
            <div className="feat yes"><i className="ti ti-check" /> Priority support</div>
            <button className="plan-btn pb-prem" onClick={startUpgrade} data-testid="plan-premium-btn">Upgrade to Premium →</button>
          </div>
        </div>
        <div className="guarantee">
          <i className="ti ti-shield-check" />
          <div className="guarantee-t"><b>30-day fair-value guarantee.</b> If Premium doesn't measurably increase your profile views in the first month, we'll refund it — no questions asked.</div>
        </div>
        <div className="testi">
          <div className="testi-b">"Within three weeks of going Premium I picked up two SME audit mandates from founders who found me in the Featured band. It paid for itself many times over."</div>
          <div className="testi-a">— Chartered Accountant, Pune · Premium advisor</div>
        </div>
      </div>

      <CheckoutDialog open={checkout} onOpenChange={setCheckout} navigate={navigate} />
    </div>
  );
}

const CheckoutDialog = ({ open, onOpenChange, navigate }) => {
  const [pricing, setPricing] = useState(null);
  const [me, setMe] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/api/matchmaker/expert/premium/pricing`, { credentials: "include" })
      .then((r) => r.json()).then(setPricing).catch(() => {});
    fetch(`${API}/api/auth/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null)).then(setMe).catch(() => {});
  }, [open]);

  const pay = async () => {
    setPaying(true);
    try {
      const ok = await loadScript("https://checkout.razorpay.com/v1/checkout.js");
      if (!ok) { toast.error("Could not load Razorpay. Please try again."); setPaying(false); return; }
      const cfg = await (await fetch(`${API}/api/payments/config`)).json();
      const orderRes = await fetch(`${API}/api/payments/create-order`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan_id: "advisor_premium",
          customer: {
            name: me?.name || "IPOLabs Advisor", email: me?.email || "advisor@ipolabs.com",
            phone: me?.mobile || "9999999999", address_line1: "India", city: "Mumbai",
            state: "Maharashtra", state_code: "27", pincode: "400001",
          },
        }),
      });
      if (!orderRes.ok) {
        const d = await orderRes.json().catch(() => ({}));
        if (String(d.detail || "").toLowerCase().includes("advisor")) {
          toast.error("Register as an advisor first to upgrade.");
          onOpenChange(false); navigate("/advisor-onboarding");
        } else { toast.error(d.detail || "Could not start checkout"); }
        setPaying(false); return;
      }
      const order = await orderRes.json();
      const rzp = new window.Razorpay({
        key: cfg.razorpay_key_id, amount: order.amount_paise, currency: "INR",
        name: "IPOLabs", description: "Advisor Premium — Featured listing (1 year)",
        order_id: order.order_id,
        prefill: { name: me?.name || "", email: me?.email || "", contact: me?.mobile || "" },
        theme: { color: "#185FA5" },
        handler: async (resp) => {
          try {
            const vr = await fetch(`${API}/api/payments/verify`, {
              method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              }),
            });
            if (!vr.ok) { toast.error("Payment could not be verified. If you were charged, contact support."); setPaying(false); return; }
            logJourney("Advisor Marketplace", "Upgraded", "Premium activated");
            toast.success("🎉 Premium activated — you're now in the Featured band!");
            onOpenChange(false); navigate("/advisor-marketplace");
          } catch { toast.error("Something went wrong confirming your payment."); }
          setPaying(false);
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rzp.open();
    } catch { toast.error("Razorpay could not open. Please try again."); setPaying(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ap-checkout-dialog">
        <DialogHeader><DialogTitle>Upgrade to Premium listing</DialogTitle><DialogDescription className="sr-only">Review pricing and complete the secure Razorpay checkout to activate your Premium advisor listing.</DialogDescription></DialogHeader>
        <div className="space-y-2" style={{ fontSize: 13 }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}><span>{pricing?.plan_name || "Premium Expert — 1 Year"}</span><b>₹{pricing?.base_price?.toLocaleString("en-IN") ?? "—"}</b></div>
          <div style={{ display: "flex", justifyContent: "space-between", color: "#8a8a94" }}><span>GST ({pricing?.gst_percent ?? 18}%)</span><span>₹{pricing?.gst_amount?.toLocaleString("en-IN") ?? "—"}</span></div>
          <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #eceef2", paddingTop: 8, fontWeight: 700 }}><span>Total</span><span>₹{pricing?.total_amount?.toLocaleString("en-IN") ?? "—"}</span></div>
          <div style={{ fontSize: 11, color: "#185FA5", background: "#eef5fc", border: ".5px solid #cfe3f4", borderRadius: 8, padding: "7px 10px", marginTop: 6 }}>
            <b>Secure checkout:</b> payment is processed by Razorpay. Premium activates automatically once your payment is verified.
          </div>
        </div>
        <DialogFooter>
          <button className="plan-btn pb-free" style={{ width: "auto", marginTop: 0 }} onClick={() => onOpenChange(false)}>Cancel</button>
          <button className="plan-btn pb-prem" style={{ width: "auto", marginTop: 0 }} disabled={paying || !pricing} onClick={pay} data-testid="ap-checkout-pay">
            {paying ? "Processing…" : `Pay ₹${pricing?.total_amount?.toLocaleString("en-IN") ?? ""}`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
